import { useState } from 'react';
import { XIcon } from './Icons';

const STORAGE_KEY = 'pdf-toolkit-word-tip-dismissed';

function wasDismissed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    // Blocked storage — show the tip rather than hide it.
    return false;
  }
}

/**
 * Points people at Word for the one conversion this tool deliberately does not
 * do: PDF back to an editable document.
 *
 * Word's built-in PDF reflow handles that well for text-heavy files and every
 * M365 user already has it, so sending people there beats shipping a
 * LibreOffice round-trip that produces a text frame per line. The scan caveat
 * is included because it is the case where Word genuinely cannot help — no OCR.
 *
 * Dismissal is a per-viewer convenience, so localStorage is the right home for
 * it and losing it is harmless.
 */
export function WordTip() {
  const [dismissed, setDismissed] = useState(wasDismissed);
  if (dismissed) return null;

  function dismiss() {
    setDismissed(true);
    try {
      localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // It will simply reappear next time.
    }
  }

  return (
    <div className="tip">
      <button className="icon-btn tip__close" onClick={dismiss} aria-label="Dismiss this tip">
        <XIcon size={13} />
      </button>
      <strong>Need to edit a PDF?</strong>
      <p>
        Word opens PDFs directly and converts them to editable documents —{' '}
        <em>File → Open</em>, then pick the PDF. No extra software needed.
      </p>
      <p className="tip__caveat">
        Works well for text-heavy files. Scanned pages come through as images, since Word cannot
        read text from a scan.
      </p>
    </div>
  );
}
